import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { type Branch, type CatalogueSnapshot, deliveryRuleSummary, type Package, type ReferenceInterval, type StudyDetail } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { money } from "../lib/format.js";
import { useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useDebounced, useQuery } from "../lib/useQuery.js";
import { LinesList, StudySearch, type Line } from "../components/StudyPicker.js";
import { Alert, Button, Card, Chip, Definition, Empty, Field, IconButton, Input, Loading, Money, PageHead, SearchInput, Select, Sheet, Switch, Tabs, TubeSwatch } from "../components/ui.js";
import { AnalytesEditor, PriceForm, type PriceChoice, StudyFichaForm } from "../components/CatalogueEditors.js";
import { AreasEditor } from "../components/AreasEditor.js";
import { AntibioticPanelsEditor } from "../components/AntibioticPanels.js";
import { DocumentTemplates } from "../components/DocumentTemplate.js";
import { AnalyteOrderEditor } from "../components/AnalyteOrderEditor.js";
import { PriceListsPanel } from "../components/PriceLists.js";
import { Icon } from "../components/icons.js";
import { RefText } from "../components/RefText.js";
import { AnalyteGroupEditor } from "../components/AnalyteGroupEditor.js";
import { studiesText, useAnalyteSearch } from "../components/AnalytePicker.js";
import { NewStudyForm } from "../components/NewStudyForm.js";
import { NewAnalyteForm } from "../components/NewAnalyteForm.js";
import { ReactivateAnalytePanel } from "../components/AnalyteRemoval.js";

/** The catalogue snapshot is ~1 MB; it is loaded once per session for this screen and kept in memory. */
let snapshotCache: CatalogueSnapshot | null = null;
function useSnapshot() {
  return useQuery(async () => {
    if (snapshotCache) return snapshotCache;
    snapshotCache = await get("catalogue");
    return snapshotCache;
  }, []);
}

/** After any catalogue edit: the next read fetches a fresh snapshot. */
function invalidateSnapshot() {
  snapshotCache = null;
}

/** The default (general) price list: what the catalogue shows until another list is chosen. */
function defaultList(snap: CatalogueSnapshot | null) {
  return snap?.price_lists.find((p) => p.is_default) ?? null;
}

/** Every active list with this study's price on it, the general list first (the price editor's choices). */
function priceChoices(snap: CatalogueSnapshot | null, studyId: string): PriceChoice[] {
  if (!snap) return [];
  return snap.price_lists
    .filter((l) => l.is_active)
    .sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name))
    .map((l) => ({ id: l.id, name: l.name, current: snap.prices.find((p) => p.price_list_id === l.id && p.study_id === studyId)?.amount_cents ?? null }));
}

type Tab = "studies" | "analytes" | "packages" | "areas" | "prices" | "antibiotics";

/**
 * Catalogue browser: studies by area, packages with their studies, and the areas' report order.
 * An Administrador edits a study's price from the list, its ficha and analytes from the study
 * page, and which studies a package contains (the client's export names packages without members).
 */
export function CataloguePage() {
  const navigate = useNavigate();
  const { can, me, refresh } = useSession();
  const [tab, setTab] = useState<Tab>("studies");
  /** The list the studies tab shows and edits prices on; "" = the general list. */
  const [listId, setListId] = useState("");
  const [editing, setEditing] = useState<Package | null>(null);
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const [areaId, setAreaId] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [pricing, setPricing] = useState<{ id: string; clave: string; name: string; amount_cents: number | null } | null>(null);
  const [creating, setCreating] = useState(false);
  const snap = useSnapshot();
  const browse = useQuery(
    () => get("catalogueBrowse", { query: { q: dq || undefined, area_id: areaId || undefined, price_list_id: listId || undefined, kind: "study", limit: 200, include_inactive: showInactive || undefined } }),
    [dq, areaId, showInactive, listId],
  );
  const admin = can("administrador");
  const activeLists = (snap.data?.price_lists ?? []).filter((l) => l.is_active).sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name));
  const list = (listId ? activeLists.find((l) => l.id === listId) : null) ?? defaultList(snap.data ?? null);
  const reloadSnapshot = () => {
    invalidateSnapshot();
    void snap.reload();
    void browse.reload();
  };

  const areas = useMemo(() => (snap.data?.areas ?? []).filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order), [snap.data]);
  const studyById = useMemo(() => new Map((snap.data?.studies ?? []).map((s) => [s.id, s])), [snap.data]);
  const priceByStudy = useMemo(() => {
    const def = snap.data?.price_lists.find((p) => p.is_default);
    const m = new Map<string, number>();
    for (const p of snap.data?.prices ?? []) if (p.price_list_id === def?.id && p.study_id) m.set(p.study_id, p.amount_cents);
    return m;
  }, [snap.data]);
  const priceByPackage = useMemo(() => {
    const def = snap.data?.price_lists.find((p) => p.is_default);
    const m = new Map<string, number>();
    for (const p of snap.data?.prices ?? []) if (p.price_list_id === def?.id && p.package_id) m.set(p.package_id, p.amount_cents);
    return m;
  }, [snap.data]);

  return (
    <>
      <PageHead title="Catálogo" lede={snap.data ? `${snap.data.studies.filter((s) => s.is_active).length} estudios · ${snap.data.packages.filter((p) => p.is_active).length} paquetes · ${areas.length} áreas. Lista de precios: ${list?.name ?? "—"}.` : "Estudios, analitos, valores de referencia, paquetes y precios."} />
      <Tabs value={tab} onChange={setTab} items={[{ value: "studies", label: "Estudios" }, { value: "analytes", label: "Analitos" }, { value: "packages", label: "Paquetes", count: snap.data?.packages.filter((p) => p.is_active).length }, { value: "areas", label: "Áreas", count: areas.length }, { value: "prices", label: "Listas de precios", count: activeLists.length }, { value: "antibiotics", label: "Antibióticos" }]} />

      {tab === "studies" && (
        <>
          <div className="toolbar">
            <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Clave o nombre…" autoFocus aria-label="Buscar estudio" />
            <Select value={areaId} onChange={(e) => setAreaId(e.target.value)} style={{ width: 260 }} aria-label="Área">
              <option value="">Todas las áreas</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            {activeLists.length > 1 && (
              <Select value={list?.id ?? ""} onChange={(e) => setListId(e.target.value)} style={{ width: 260 }} aria-label="Lista de precios">
                {activeLists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.is_default ? `${l.name} (general)` : l.name}
                  </option>
                ))}
              </Select>
            )}
            {admin && (
              <Switch checked={showInactive} onChange={setShowInactive}>
                Mostrar inactivos
              </Switch>
            )}
            {admin && snap.data && (
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                Nuevo estudio
              </Button>
            )}
          </div>
          <Card pad={false}>
            {browse.loading && !browse.data ? (
              <Loading />
            ) : !browse.data?.items.length ? (
              <Empty icon="book" title="Sin estudios" hint="Prueba con otra clave o área." />
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Clave</th>
                      <th>Estudio</th>
                      <th>Área</th>
                      <th>Recipiente</th>
                      <th className="right">Analitos</th>
                      <th className="right">Precio</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {browse.data.items.map((it) => {
                      const s = studyById.get(it.id);
                      return (
                        <tr key={it.id} className="clickable" onClick={() => navigate(`/catalogo/estudios/${it.id}`)}>
                          <td className="mono">{it.clave}</td>
                          <td style={{ fontWeight: 500 }}>
                            {it.name}
                            {s && !s.is_active && (
                              <>
                                {" "}
                                <Chip>Inactivo</Chip>
                              </>
                            )}
                          </td>
                          <td className="muted">{it.area_name ?? "—"}</td>
                          <td>{it.container_colour_hex ? <span className="tube-cap" style={{ background: it.container_colour_hex, display: "inline-block" }} /> : <span className="faint">—</span>}</td>
                          <td className="right tnum muted">{s ? s.analytes.filter((a) => a.is_active && a.kind !== "subtitle").length : ""}</td>
                          <td className="right">
                            <span className="row end" style={{ gap: 4 }}>
                              {it.amount_cents !== null ? <Money cents={it.amount_cents} /> : <span className="faint">—</span>}
                              {admin && list && (
                                <IconButton
                                  size="sm"
                                  icon="pen"
                                  label={`Cambiar precio de ${it.clave}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPricing({ id: it.id, clave: it.clave, name: it.name, amount_cents: it.amount_cents });
                                  }}
                                />
                              )}
                            </span>
                          </td>
                          <td className="right">
                            <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {tab === "packages" &&
        (snap.loading && !snap.data ? (
          <Loading />
        ) : (
          <div className="stack-lg">
            {(snap.data?.packages ?? [])
              .filter((p) => p.is_active)
              .map((p) => {
                const studies = p.study_ids.map((id) => studyById.get(id)).filter(Boolean);
                const sum = studies.reduce((s, st) => s + (priceByStudy.get(st!.id) ?? 0), 0);
                const price = priceByPackage.get(p.id);
                return (
                  <Card
                    key={p.id}
                    title={
                      <span className="row" style={{ gap: 12 }}>
                        <span className="mono muted">{p.clave}</span>
                        <span className="h3">{p.name}</span>
                      </span>
                    }
                    actions={
                      <span className="row" style={{ gap: 14 }}>
                        {price !== undefined && <Money cents={price} large />}
                        {price !== undefined && sum > price && studies.length > 0 && <Chip tone="green">ahorra {money(sum - price)}</Chip>}
                        {can("administrador") && (
                          <Button size="sm" variant="secondary" icon="pen" onClick={() => setEditing(p)}>
                            {studies.length ? "Editar estudios" : "Definir estudios"}
                          </Button>
                        )}
                      </span>
                    }
                    pad={false}
                  >
                    {studies.length === 0 && (
                      <div style={{ padding: 18 }}>
                        <Alert tone="amber">
                          <strong>Sin estudios asignados.</strong> Este paquete no se puede cotizar ni registrar hasta definir qué estudios incluye.
                        </Alert>
                      </div>
                    )}
                    {studies.map((st) => (
                      <Link key={st!.id} to={`/catalogo/estudios/${st!.id}`} className="list-row clickable" style={{ minHeight: 52 }}>
                        <span className="mono muted" style={{ width: 110 }}>
                          {st!.clave}
                        </span>
                        <span className="grow">{st!.name}</span>
                        <span className="muted">{priceByStudy.has(st!.id) ? money(priceByStudy.get(st!.id)!) : ""}</span>
                      </Link>
                    ))}
                  </Card>
                );
              })}
          </div>
        ))}

      <Sheet open={pricing !== null} onClose={() => setPricing(null)} title={pricing ? `Precio de ${pricing.clave} · ${pricing.name}` : ""}>
        {pricing && list && (
          <PriceForm
            key={pricing.id}
            title={pricing.clave}
            priceListId={list.id}
            priceListName={list.name}
            studyId={pricing.id}
            current={pricing.amount_cents}
            choices={priceChoices(snap.data ?? null, pricing.id)}
            onCancel={() => setPricing(null)}
            onSaved={() => {
              setPricing(null);
              invalidateSnapshot();
              void snap.reload();
              void browse.reload();
            }}
          />
        )}
      </Sheet>

      <Sheet open={creating} onClose={() => setCreating(false)} title="Nuevo estudio" wide>
        {creating && snap.data && (
          <NewStudyForm
            lookups={snap.data}
            claves={snap.data.studies.map((st) => st.clave)}
            priceList={defaultList(snap.data)}
            onCancel={() => setCreating(false)}
            onSaved={(st) => {
              setCreating(false);
              invalidateSnapshot();
              navigate(`/catalogo/estudios/${st.id}?editar=analitos`);
            }}
          />
        )}
      </Sheet>

      {tab === "analytes" && <AnalytesTab admin={admin} branches={me?.branches ?? []} studies={snap.data?.studies ?? []} onChanged={reloadSnapshot} />}

      <PackageSheet
        pkg={editing}
        snapshot={snap.data ?? null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          invalidateSnapshot();
          void snap.reload();
          setEditing(null);
        }}
      />

      {tab === "areas" && (
        <AreasEditor
          areas={snap.data?.areas ?? []}
          studyCount={(areaId) => (snap.data?.studies ?? []).filter((s) => s.area_id === areaId && s.is_active).length}
          admin={admin}
          onChanged={reloadSnapshot}
        />
      )}

      {tab === "antibiotics" && <AntibioticPanelsEditor panels={snap.data?.antibiotic_panels} studies={snap.data?.studies ?? []} admin={admin} onChanged={reloadSnapshot} />}

      {tab === "prices" && (
        <PriceListsPanel
          lists={snap.data?.price_lists ?? []}
          priceCount={(id) => (snap.data?.prices ?? []).filter((p) => p.price_list_id === id).length}
          branches={me?.branches ?? []}
          admin={admin}
          onChanged={reloadSnapshot}
          onBranchesChanged={refresh}
          onEditPrices={(id) => {
            setListId(id);
            setTab("studies");
          }}
        />
      )}
    </>
  );
}

/** Compose a package: pick the studies (in report order) and save through `catalogue.package.upsert`. */
function PackageSheet({ pkg, snapshot, onClose, onSaved }: { pkg: Package | null; snapshot: CatalogueSnapshot | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pkg || !snapshot) return;
    const def = snapshot.price_lists.find((p) => p.is_default);
    setName(pkg.name);
    setLines(
      pkg.study_ids.flatMap((id) => {
        const st = snapshot.studies.find((s) => s.id === id);
        if (!st) return [];
        return [
          {
            kind: "study" as const,
            id: st.id,
            clave: st.clave,
            name: st.name,
            area_name: snapshot.areas.find((a) => a.id === st.area_id)?.name ?? null,
            amount_cents: snapshot.prices.find((p) => p.price_list_id === def?.id && p.study_id === st.id)?.amount_cents ?? null,
            price_id: null,
            container_colour_hex: snapshot.containers.find((c) => c.id === st.container_id)?.colour_hex ?? null,
            indications: st.indications,
            study_claves: [],
            specimens: [],
          },
        ];
      }),
    );
  }, [pkg, snapshot]);

  async function save() {
    if (!pkg) return;
    setBusy(true);
    try {
      await run("catalogue.package.upsert", { package_id: pkg.id, clave: pkg.clave, name: name.trim() || pkg.name, is_active: pkg.is_active, study_ids: lines.map((l) => l.id) });
      toast.success(lines.length ? `${pkg.clave}: ${lines.length} ${lines.length === 1 ? "estudio" : "estudios"} guardados.` : `${pkg.clave} quedó sin estudios.`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={pkg !== null}
      onClose={onClose}
      title={pkg ? `Estudios del paquete ${pkg.clave}` : ""}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            Guardar paquete
          </Button>
        </>
      }
    >
      <p className="small muted">Los estudios se imprimen en el informe en este orden. El precio del paquete se mantiene en la lista de precios.</p>
      <Field label="Nombre del paquete">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <StudySearch studiesOnly selected={lines} onPick={(it) => setLines((ls) => [...ls, it])} autoFocus />
      <LinesList lines={lines} onRemove={(l) => setLines((ls) => ls.filter((x) => x.id !== l.id))} />
    </Sheet>
  );
}

function intervalScope(r: ReferenceInterval): string {
  const parts: string[] = [];
  if (r.sex) parts.push(r.sex === "F" ? "Mujeres" : "Hombres");
  if (r.age_min_days !== null || r.age_max_days !== null) {
    const fmt = (d: number) => (d >= 365 ? `${Math.round(d / 365)} a` : d >= 30 ? `${Math.round(d / 30)} m` : `${d} d`);
    parts.push(`${r.age_min_days !== null ? fmt(r.age_min_days) : "0"} – ${r.age_max_days !== null ? fmt(r.age_max_days) : "∞"}`);
  }
  if (r.condition) parts.push(r.condition);
  return parts.join(" · ") || "General";
}

/**
 * An analyte's reference values on the study page: general first, then each branch with its own
 * (ADR 0020). Within a set, one chip per row with its range, and the printed text once: rows
 * structured from one laboratory text all print that same text.
 */
export function IntervalsSummary({ rows, branches }: { rows: ReferenceInterval[]; branches: { id: string; name: string }[] }) {
  const sorted = rows.slice().sort((x, y) => x.sort_order - y.sort_order);
  const sets = [...new Set(sorted.map((r) => r.branch_id ?? null))].sort((x, y) => (x === null ? -1 : y === null ? 1 : 0));
  const range = (r: ReferenceInterval) => (r.low !== null || r.high !== null ? `${r.low ?? ""} – ${r.high ?? ""}` : null);
  return (
    <div className="stack" style={{ gap: 8 }}>
      {sets.map((set) => {
        const own = sorted.filter((r) => (r.branch_id ?? null) === set);
        const texts = [...new Set(own.map((r) => r.display_text ?? range(r) ?? r.expected_text ?? "").filter(Boolean))];
        const sharedText = own.length > 1 && texts.length === 1;
        const name = set === null ? null : (branches.find((b) => b.id === set)?.name ?? "otra sucursal");
        return (
          <div key={set ?? "general"} className="stack" style={{ gap: 4 }}>
            {(sets.length > 1 || set !== null) && <span className="small muted">{set === null ? "Valores generales" : `Solo en ${name}`}</span>}
            {own.map((r) => (
              <div key={r.id} className="row small" style={{ gap: 10, alignItems: "flex-start" }}>
                <Chip tone={r.condition ? "violet" : r.sex === "F" ? "violet" : r.sex === "M" ? "blue" : "neutral"}>{intervalScope(r)}</Chip>
                {sharedText ? <span className="tnum">{range(r) ?? r.expected_text ?? ""}</span> : <RefText text={r.display_text ?? range(r) ?? r.expected_text ?? ""} />}
              </div>
            ))}
            {sharedText && <RefText text={texts[0]} className="small muted" />}
          </div>
        );
      })}
    </div>
  );
}

type StudyEdit = "ficha" | "analytes" | "order" | "price" | "active" | null;

export function StudyPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { can, me } = useSession();
  const admin = can("administrador");
  const branches = me?.branches ?? [];
  const [params, setParams] = useSearchParams();
  const [edit, setEdit] = useState<StudyEdit>(() => (params.get("editar") === "analitos" ? "analytes" : null));
  const study = useQuery(() => get("study", { params: { id } }), [id]);
  useEffect(() => {
    // The flag only opens the editor once, right after «Nuevo estudio».
    if (params.has("editar")) setParams({}, { replace: true });
  }, [params, setParams]);
  const snap = useSnapshot();
  if (study.loading && !study.data) return <Loading />;
  if (!study.data) return <Empty icon="warning" title="No se encontró el estudio" hint={study.error ?? undefined} />;
  const s = study.data;
  const name = (xs: { id: string; name: string }[] | undefined, id: string | null) => (id ? (xs ? (xs.find((x) => x.id === id)?.name ?? "—") : "…") : "—");
  const container = snap.data?.containers.find((c) => c.id === s.container_id);
  const def = defaultList(snap.data ?? null);
  const price = snap.data?.prices.find((p) => p.price_list_id === def?.id && p.study_id === s.id);
  const analytes = s.analytes.filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const overrides = s.branch_overrides ?? [];
  const saved = () => {
    setEdit(null);
    invalidateSnapshot();
    void snap.reload();
    void study.reload();
  };

  return (
    <>
      <PageHead
        kicker={`Estudio · ${s.clave}`}
        title={s.name}
        lede={s.indications ?? undefined}
        actions={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => navigate("/catalogo")}>
              Catálogo
            </Button>
            {price && <Money cents={price.amount_cents} large />}
            {admin && def && (
              <Button variant="secondary" icon="tag" onClick={() => setEdit("price")}>
                {price ? "Cambiar precio" : "Poner precio"}
              </Button>
            )}
            {admin && (
              <Button variant={s.is_active ? "ghost" : "primary"} icon={s.is_active ? "x" : "check"} onClick={() => setEdit("active")}>
                {s.is_active ? "Quitar del catálogo" : "Volver a activar"}
              </Button>
            )}
          </>
        }
      />
      {!s.is_active && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="amber">Este estudio está inactivo: no se puede cotizar ni registrar.</Alert>
        </div>
      )}
      <div className="split narrow-left">
        <Card
          title="Ficha"
          actions={
            admin && (
              <Button size="sm" variant="secondary" icon="pen" onClick={() => setEdit("ficha")}>
                Editar ficha
              </Button>
            )
          }
        >
          <div className="stack" style={{ gap: 14 }}>
            <Definition k="Área" v={name(snap.data?.areas, s.area_id)} />
            <Definition k="Tipo de muestra" v={name(snap.data?.sample_types, s.sample_type_id)} />
            <Definition k="Recipiente" v={container ? <TubeSwatch hex={container.colour_hex} name={container.name} /> : "—"} />
            <Definition k="Método" v={name(snap.data?.methods, s.method_id)} />
            <Definition k="Técnica" v={name(snap.data?.techniques, s.technique_id)} />
            <Definition k="Equipo" v={name(snap.data?.equipment, s.equipment_id)} />
            {branches.length > 1 || overrides.length > 0 ? (
              <div className="stack" style={{ gap: 6 }} role="group" aria-label="Por sucursal">
                <span className="kicker">Por sucursal</span>
                {branches.map((b) => {
                  const o = overrides.find((x) => x.branch_id === b.id);
                  const parts = o
                    ? [
                        o.method_id && `Método: ${name(snap.data?.methods, o.method_id)}`,
                        o.technique_id && `Técnica: ${name(snap.data?.techniques, o.technique_id)}`,
                        o.equipment_id && `Equipo: ${name(snap.data?.equipment, o.equipment_id)}`,
                      ].filter(Boolean)
                    : [];
                  return <Definition key={b.id} k={b.name} v={parts.length ? parts.join(" · ") : <span className="faint">Igual que el estudio</span>} />;
                })}
              </div>
            ) : null}
            <Definition k="Entrega" v={deliveryRuleSummary(s)} />
            <Definition k="Tipo" v={s.kind === "analytic" ? "Analítico" : "Servicio"} />
            <Definition k="Versión" v={`v${s.version}`} />
          </div>
        </Card>
        <Card
          title={`${edit === "order" ? "Ordenar analitos" : "Analitos"} (${analytes.filter((a) => a.kind !== "subtitle").length})`}
          actions={
            admin &&
            edit !== "order" && (
              <span className="row wrap" style={{ gap: 8, justifyContent: "flex-end" }}>
                {analytes.length > 1 && (
                  <Button size="sm" variant="secondary" icon="grip" onClick={() => setEdit("order")}>
                    Ordenar
                  </Button>
                )}
                <Button size="sm" variant="secondary" icon="pen" onClick={() => setEdit("analytes")}>
                  Editar analitos
                </Button>
              </span>
            )
          }
          pad={false}
        >
          {edit === "order" ? (
            <AnalyteOrderEditor study={s} onSaved={saved} onCancel={() => setEdit(null)} />
          ) : analytes.length === 0 ? (
            <div className="list-row faint">Este estudio no tiene analitos definidos.</div>
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Parámetro</th>
                    <th>Tipo</th>
                    <th>Unidad</th>
                    <th>Valores de referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {analytes.map((a) =>
                    a.kind === "subtitle" ? (
                      <tr key={a.id} style={{ background: "var(--paper-2)" }}>
                        <td colSpan={4} style={{ fontWeight: 600, color: "var(--ink-2)" }}>
                          {a.name}
                        </td>
                      </tr>
                    ) : (
                      <tr key={a.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{a.name}</div>
                          <div className="small faint mono">{a.code}{a.formula ? ` · = ${a.formula}` : ""}</div>
                          {(a.branch_formulas ?? []).map((b) => (
                            <div key={b.branch_id} className="small faint">
                              {`En ${branches.find((x) => x.id === b.branch_id)?.name ?? "otra sucursal"}: `}
                              {b.formula === null ? "se captura" : <span className="mono">= {b.formula}</span>}
                            </div>
                          ))}
                        </td>
                        <td className="muted">{{ numeric: "Numérico", text: "Texto", reference: "Opciones", document: "Documento", subtitle: "" }[a.kind]}{a.kind === "reference" && a.options ? <div className="small faint">{a.options.join(" / ")}</div> : null}</td>
                        <td className="muted">{a.unit ?? ""}</td>
                        <td>
                          {a.reference_intervals.length === 0 ? <span className="faint">—</span> : <IntervalsSummary rows={a.reference_intervals} branches={branches} />}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <div className="stack" style={{ gap: 16, marginTop: 16 }}>
        <DocumentTemplates study={s} admin={admin} onSaved={saved} />
      </div>

      <Sheet open={edit === "ficha"} onClose={() => setEdit(null)} title={`Ficha de ${s.clave}`} wide>
        {edit === "ficha" && snap.data && <StudyFichaForm study={s} lookups={snap.data} branches={branches} onSaved={saved} onCancel={() => setEdit(null)} />}
      </Sheet>
      <Sheet open={edit === "analytes"} onClose={() => setEdit(null)} title={`Analitos de ${s.clave}`} wide>
        {edit === "analytes" && <AnalytesEditor study={s} branches={branches} onSaved={saved} onCancel={() => setEdit(null)} />}
      </Sheet>
      <Sheet open={edit === "active"} onClose={() => setEdit(null)} title={s.is_active ? `Quitar ${s.clave} del catálogo` : `Volver a activar ${s.clave}`}>
        {edit === "active" && (
          <StudyActiveForm study={s} packages={(snap.data?.packages ?? []).filter((p) => p.is_active && p.study_ids.includes(s.id))} onSaved={saved} onCancel={() => setEdit(null)} />
        )}
      </Sheet>
      <Sheet open={edit === "price"} onClose={() => setEdit(null)} title={`Precio de ${s.clave}`}>
        {edit === "price" && def && (
          <PriceForm title={s.clave} priceListId={def.id} priceListName={def.name} studyId={s.id} current={price?.amount_cents ?? null} choices={priceChoices(snap.data ?? null, s.id)} onSaved={saved} onCancel={() => setEdit(null)} />
        )}
      </Sheet>
    </>
  );
}

/**
 * «Quitar del catálogo» / «Volver a activar» (pilot request of 2026-11-03, "una opción para
 * agregar algún estudio o eliminar alguno"). Nothing is deleted: the study stops being offered,
 * its orders and results stay as they are, and «Mostrar inactivos» brings it back. A study that
 * an active package still contains is named here, and removing it anyway is an explicit choice.
 */
function StudyActiveForm({ study, packages, onSaved, onCancel }: { study: StudyDetail; packages: Package[]; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const off = study.is_active;
  async function save() {
    setBusy(true);
    try {
      await run<"catalogue.study.set_active", StudyDetail>("catalogue.study.set_active", { study_id: study.id, is_active: !off, force_packages: off && packages.length > 0 });
      toast.success(off ? `${study.clave} ya no aparece en el catálogo.` : `${study.clave} está activo otra vez.`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack" style={{ gap: 14 }}>
      <p style={{ margin: 0 }}>
        <strong>
          {study.clave} · {study.name}
        </strong>
      </p>
      {off ? (
        <ul className="stack small" style={{ gap: 6, margin: 0, paddingLeft: 18 }}>
          <li>Ya no se podrá cotizar ni registrar en órdenes nuevas.</li>
          <li>Las órdenes ya registradas, sus resultados e informes no cambian.</li>
          <li>No se borra: con «Mostrar inactivos» lo encuentras y lo puedes volver a activar.</li>
        </ul>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Se podrá cotizar y registrar otra vez, con sus analitos y su precio de antes.
        </p>
      )}
      {off && packages.length > 0 && (
        <Alert tone="amber">
          Está en {packages.length === 1 ? "el paquete" : "los paquetes"} {packages.map((p) => `${p.clave} · ${p.name}`).join(", ")}. El paquete lo seguirá incluyendo hasta que lo quites de él.
        </Alert>
      )}
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant={off ? "danger" : "primary"} icon={off ? "x" : "check"} busy={busy} onClick={() => void save()}>
          {off ? (packages.length ? "Quitar de todos modos" : "Quitar del catálogo") : "Volver a activar"}
        </Button>
      </div>
    </div>
  );
}

const KIND_NAME: Record<string, string> = { numeric: "Numérico", text: "Texto", reference: "Opciones", subtitle: "Subtítulo", document: "Documento" };

/**
 * Catálogo → Analitos (pilot request of 2026-11-03, "un apartado para entrar y solo modificar los
 * analitos, y no buscarlos por estudio"): every analyte by clave, searchable by any word of its
 * name, clave or unit, with the studies that carry it. Tapping one opens its editor
 * (Administrador) or the list of its studies.
 */
function AnalytesTab({ admin, branches, studies, onChanged }: { admin: boolean; branches: Branch[]; studies: { id: string; clave: string; name: string; is_active: boolean }[]; onChanged: () => void }) {
  const [q, setQ] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const list = useAnalyteSearch(q, 200, showDeleted);
  const [open, setOpen] = useState<{ code: string; name: string; is_deleted: boolean; studies: { id: string; clave: string; name: string; is_active: boolean }[] } | null>(null);
  const [creating, setCreating] = useState(false);
  const done = () => {
    setOpen(null);
    setCreating(false);
    onChanged();
    void list.reload();
  };
  return (
    <>
      <div className="toolbar">
        <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, clave o unidad del analito…" autoFocus aria-label="Buscar analito" />
        <label className="row small" style={{ gap: 8, minHeight: 44 }}>
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} style={{ width: 22, height: 22 }} />
          Mostrar eliminados
        </label>
        {admin && (
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            Nuevo analito
          </Button>
        )}
      </div>
      <Card pad={false}>
        {list.loading && !list.data ? (
          <Loading />
        ) : !list.data?.items.length ? (
          <Empty icon="search" title="Sin analitos" hint="Prueba con otra palabra del nombre o la clave." />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Clave</th>
                  <th>Analito</th>
                  <th>Unidad</th>
                  <th>Tipo</th>
                  <th className="right">Estudios</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((g) => (
                  <tr key={g.code} className="clickable" onClick={() => setOpen(g)}>
                    <td className="mono">{g.code}</td>
                    <td style={{ fontWeight: 500 }}>
                      {g.name}
                      {g.is_deleted && (
                        <>
                          {" "}
                          <Chip>Eliminado</Chip>
                        </>
                      )}
                      {g.variant_count > 1 && (
                        <>
                          {" "}
                          <Chip tone="amber">{g.variant_count} versiones</Chip>
                        </>
                      )}
                    </td>
                    <td className="muted">{g.unit ?? ""}</td>
                    <td className="muted">{KIND_NAME[g.kind]}</td>
                    <td className="right">
                      <span className="tnum">{g.study_count}</span>
                      <div className="small faint">{studiesText(g.studies, 3)}</div>
                    </td>
                    <td className="right">
                      <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.data.next_cursor && <div className="list-row small muted">Se muestran los primeros {list.data.items.length}. Escribe para buscar.</div>}
          </div>
        )}
      </Card>
      <Sheet open={creating} onClose={() => setCreating(false)} title="Nuevo analito" wide>
        {creating && <NewAnalyteForm studies={studies} branches={branches} onCancel={() => setCreating(false)} onSaved={done} />}
      </Sheet>
      <Sheet open={open !== null} onClose={() => setOpen(null)} title={open ? `${open.code} · ${open.name}` : ""} wide>
        {open &&
          (admin && open.is_deleted ? (
            <ReactivateAnalytePanel code={open.code} name={open.name} studies={open.studies} onCancel={() => setOpen(null)} onDone={done} />
          ) : admin ? (
            <AnalyteGroupEditor code={open.code} branches={branches} onCancel={() => setOpen(null)} onSaved={done} />
          ) : (
            <div className="stack" style={{ gap: 6 }}>
              {open.studies.map((st) => (
                <Link key={st.id} to={`/catalogo/estudios/${st.id}`} className="list-row clickable" style={{ minHeight: 52 }}>
                  <span className="mono muted" style={{ width: 110 }}>
                    {st.clave}
                  </span>
                  <span className="grow">{st.name}</span>
                </Link>
              ))}
            </div>
          ))}
      </Sheet>
    </>
  );
}
